import type { WalletGroupedAccounts } from '@polaris/core/models'
import { useAtomValue, useSetAtom } from 'jotai'
import { motion } from 'motion/react'
import Image from 'next/image'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { useAmplitudeAnalytics } from '@/src/hooks/use-amplitude-analytics'
import { useAccountsActions } from '@/src/stores/accounts/actions'
import { useAllDerivedAddresses } from '@/src/stores/accounts/addresses'
import {
  dismissedToastsAtom,
  usePendingAccountActions,
} from '@/src/stores/accounts/detection'
import { walletAccountsAtom } from '@/src/stores/accounts/wallets'
import { walletOptionsAtom } from '@/src/stores/wallets/atoms'
import { cn } from '@/src/utils/styles'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog'
import {
  ListRow,
  ListRowButton,
  ListRowSide,
  ListRowSideName,
} from '../ui/list-row'
import { showNewWalletConnectedToast } from '../ui/toast'
import { AccountCard } from './account-card'
import { AddressesDialog } from './addresses-dialog'
import { ChangeAccountDialog } from './change-account'

interface ManageWalletProps {
  wallet: WalletGroupedAccounts
  onBack?: () => void
  onEditWalletName?: () => void
  classNames?: {
    buttonsContainer?: string
  }
}

export function ManageWallet({
  wallet,
  onBack,
  onEditWalletName,
  classNames,
}: ManageWalletProps) {
  const {
    name,
    grouping_key,
    provider_info,
    accounts,
    walletConnectionStatus,
  } = wallet

  const [isAddressesDialogOpen, setIsAddressesDialogOpen] = useState(false)
  const { removeAccounts } = useAccountsActions()
  const { data: addresses = [] } = useAllDerivedAddresses(grouping_key)
  const { aggregatedWallets } = useAtomValue(walletOptionsAtom)
  const { groupedAccounts } = useAtomValue(walletAccountsAtom)
  const [showChangeAccountDialog, setShowChangeAccountDialog] = useState(false)
  const { ampli } = useAmplitudeAnalytics()
  const { cleanupRemovedAccount } = usePendingAccountActions()
  const setDismissedToasts = useSetAtom(dismissedToastsAtom)

  /**
   * If this is populated, it means some ecosystem(s)
   * is/are connected to this same wallet, but for a different account.
   */
  const fromSameWalletGroupedAccounts = useMemo(() => {
    const otherWalletGroupedAccounts = groupedAccounts.filter(
      (group) =>
        group.grouping_key !== grouping_key &&
        group.provider_info.name === provider_info.name,
    )

    // First one to have some wallet connection (in any ecosystem)
    return otherWalletGroupedAccounts.find(
      (group) =>
        group.walletConnectionStatus === 'some' ||
        group.walletConnectionStatus === 'all',
    )
  }, [groupedAccounts, grouping_key, provider_info.name])

  const isWatchAddress = wallet.type === 'WatchAddress'
  const isMobile = wallet.type === 'Mobile'
  const aggregatedWallet = aggregatedWallets.find(
    (wallet) => wallet.displayName === provider_info.name,
  )

  // Close the dialog once accounts are switched to this one externally (in wallet)
  // biome-ignore lint/correctness/useExhaustiveDependencies: We just want to run effect for those two deps
  useEffect(() => {
    if (!fromSameWalletGroupedAccounts && showChangeAccountDialog) {
      setShowChangeAccountDialog(false)

      const toastId = `${wallet.provider_info.name}-${wallet.grouping_key}`

      showNewWalletConnectedToast({
        toastId,
        walletImg: wallet.provider_info.img,
        walletName: wallet.provider_info.name,
        accountName: wallet.name,
        onDismiss: () => {
          toast.dismiss(toastId)
          setDismissedToasts((prev) => {
            const newSet = new Set(prev)
            newSet.add(wallet.grouping_key)
            return newSet
          })
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromSameWalletGroupedAccounts, showChangeAccountDialog])

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className='flex flex-col'
    >
      <AccountCard
        account={wallet}
        className='w-full mx-auto shadow-xl my-2 xl:mt-0'
        textContentClassNames='text-base'
        onClick={onBack}
      />

      {/* Add funds section - disabled for now */}
      {/* 
      <div className='flex items-center gap-6 justify-center pt-6'>
        <div className='flex flex-col gap-2 items-center'>
          <Button
            type='button'
            size='md'
            variant='primary'
            className='size-12'
          >
            <ArrowDownToLine
              size={24}
              className='text-button-primary-text min-w-6'
            />
          </Button>
          <span className='text-primary text-xs font-semibold'>
            Add funds
          </span>
        </div>
        <div className='flex flex-col gap-2 items-center'>
          <Button
            type='button'
            size='md'
            variant='secondary'
            className='size-12'
          >
            <CreditCard
              size={24}
              className='text-button-secondary-text min-w-6'
            />
          </Button>
          <span className='text-primary text-xs font-semibold'>
            Add cash
          </span>
        </div>
      </div>
      */}

      <div className='flex flex-col pt-6 pb-2 -mx-4'>
        <ListRowButton
          size='md'
          className='py-3'
          disabled={addresses.length === 0}
          onClick={() => {
            ampli?.walletViewWalletChainAddresses({
              wallet: provider_info.name,
            })
            setIsAddressesDialogOpen(true)
          }}
        >
          <ListRowSide className='flex-row items-center gap-4'>
            <ListRowSideName>View addresses</ListRowSideName>
          </ListRowSide>
          <ListRowSide showIndicator>
            <span className='text-secondary-100'>{addresses.length}</span>
          </ListRowSide>
        </ListRowButton>

        {onEditWalletName ? (
          <ListRowButton size='md' className='py-3' onClick={onEditWalletName}>
            <ListRowSide className='flex-row items-center gap-4'>
              <ListRowSideName>Wallet name</ListRowSideName>
            </ListRowSide>
            <ListRowSide showIndicator>
              <span className='text-secondary-100'>{name}</span>
            </ListRowSide>
          </ListRowButton>
        ) : (
          <ListRow size='md' className='py-3'>
            <ListRowSide className='flex-row items-center gap-4'>
              <ListRowSideName>Wallet name</ListRowSideName>
            </ListRowSide>
            <ListRowSide>
              <span className='text-secondary-100'>{name}</span>
            </ListRowSide>
          </ListRow>
        )}

        {!isWatchAddress && !isMobile && (
          <ListRow size='md' className='py-3 max-h-12'>
            <ListRowSide className='flex-row items-center gap-4'>
              <ListRowSideName>
                {walletConnectionStatus === 'none' ||
                fromSameWalletGroupedAccounts
                  ? 'Last connected with'
                  : 'Connected with'}
              </ListRowSideName>
            </ListRowSide>
            <Badge
              size='large'
              className='bg-none border-border1 pl-2'
              leftIcon={
                provider_info.img ? (
                  <Image
                    src={provider_info.img}
                    alt={name}
                    width={16}
                    height={16}
                  />
                ) : undefined
              }
            >
              <span>{provider_info.name}</span>
            </Badge>
          </ListRow>
        )}
      </div>

      <div
        className={cn(
          'flex flex-col gap-3 py-4 w-full',
          classNames?.buttonsContainer,
        )}
      >
        {!isWatchAddress &&
          (walletConnectionStatus === 'none' ||
          fromSameWalletGroupedAccounts ? (
            <Button
              type='button'
              size='md'
              variant='secondary'
              className='w-full font-semibold'
              disabled={!aggregatedWallet && !fromSameWalletGroupedAccounts}
              onClick={async () => {
                if (fromSameWalletGroupedAccounts) {
                  setShowChangeAccountDialog(true)
                  return
                }

                if (aggregatedWallet) {
                  for (const account of accounts) {
                    if (account.connectedWallet) {
                      account.connectedWallet.disconnect()
                    }
                  }
                  aggregatedWallet.connect?.()
                }
              }}
            >
              Reconnect wallet
            </Button>
          ) : (
            <Button
              type='button'
              size='md'
              variant='secondary'
              className='w-full font-semibold'
              disabled={removeAccounts.isPending || removeAccounts.isSuccess}
              onClick={() => {
                for (const account of accounts) {
                  if (account.connectedWallet) {
                    account.connectedWallet.disconnect()
                  }
                }
              }}
            >
              Disconnect wallet
            </Button>
          ))}

        <Dialog>
          <DialogTrigger asChild>
            <Button
              type='button'
              size='md'
              variant='danger'
              className='w-full font-semibold'
              disabled={removeAccounts.isPending || removeAccounts.isSuccess}
            >
              Remove from portfolio
            </Button>
          </DialogTrigger>
          <DialogContent
            aria-describedby={undefined}
            showBackButton={false}
            showCloseButton={false}
            className='max-w-[320px] p-6 h-min'
          >
            <DialogHeader>
              <DialogTitle className='text-xl mb-2'>
                Remove wallet account from your porfolio?
              </DialogTitle>
            </DialogHeader>
            <DialogDescription className='text-sm mb-6'>
              Your balances will no longer be tracked for this wallet account.
            </DialogDescription>

            <div className='flex flex-col items-center gap-3'>
              <DialogClose asChild>
                <Button
                  variant='danger'
                  size='lg'
                  autoFocus={false}
                  className='w-full'
                  disabled={
                    removeAccounts.isPending || removeAccounts.isSuccess
                  }
                  onClick={() => {
                    if (
                      aggregatedWallet &&
                      wallet.walletConnectionStatus !== 'none'
                    )
                      aggregatedWallet.disconnect?.()
                    removeAccounts.mutate(accounts)
                    onBack?.()
                    cleanupRemovedAccount({
                      providerName: provider_info.name,
                      groupingKey: grouping_key,
                    })

                    ampli?.walletRemoveFromPortfolio({
                      wallet: provider_info.name,
                    })
                  }}
                >
                  Remove
                </Button>
              </DialogClose>
              <DialogClose asChild>
                <Button
                  variant='secondary'
                  size='lg'
                  className='w-full'
                  autoFocus={true}
                >
                  Cancel
                </Button>
              </DialogClose>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <AddressesDialog
        isOpen={isAddressesDialogOpen}
        onOpenChange={setIsAddressesDialogOpen}
        wallet={wallet}
      />
      <ChangeAccountDialog
        isOpen={showChangeAccountDialog}
        onOpenChange={setShowChangeAccountDialog}
        leftWalletName={fromSameWalletGroupedAccounts?.provider_info.name}
        leftWalletIcon={fromSameWalletGroupedAccounts?.provider_info.img}
        rightWalletName={provider_info.name}
        rightWalletIcon={provider_info.img}
        leftAccountName={fromSameWalletGroupedAccounts?.name}
        rightAccountName={name}
      />
    </motion.div>
  )
}
